import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PortalUser, AuthState, LoginCredentials, AuthResponse } from '../types';
import { getApiBaseUrl } from '../../config/api';

interface PortalAuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

const TOKEN_KEY = 'portal_token';
const REFRESH_TOKEN_KEY = 'portal_refresh_token';
const USER_KEY = 'portal_user';

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Restore session from localStorage. Shared by the normal startup path and the
  // SSO fallback when no/invalid code is present.
  const restoreFromStorage = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userJson = localStorage.getItem(USER_KEY);

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson) as PortalUser;
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
        return;
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  // Initialize auth state. Supports the cross-origin SSO handoff (#448): the
  // website redirects here with a short-lived, single-use ?code=, which we
  // exchange for a real session via POST /portal/sso/exchange. The full JWT
  // never travels in the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (code) {
      // Scrub the code from the URL immediately so it can't be re-shared or
      // captured from history; it's single-use server-side regardless.
      params.delete('code');
      const clean = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));

      (async () => {
        try {
          const response = await fetch(`${getApiBaseUrl()}/portal/sso/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code }),
          });
          if (!response.ok) {
            throw new Error('SSO exchange failed');
          }
          const data: AuthResponse = await response.json();
          localStorage.setItem(TOKEN_KEY, data.token);
          localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
          setState({
            user: data.user,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Failed exchange (expired/used/invalid) — fall through to whatever
          // session is already in localStorage, else the login screen.
          restoreFromStorage();
        }
      })();
      return;
    }

    restoreFromStorage();
  }, [restoreFromStorage]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await fetch(`${getApiBaseUrl()}/portal/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data: AuthResponse = await response.json();

      // Store tokens and user
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));

      setState({
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const refreshAuth = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      logout();
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/portal/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data: AuthResponse = await response.json();

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));

      setState({
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      logout();
    }
  }, [logout]);

  return (
    <PortalAuthContext.Provider value={{ ...state, login, logout, refreshAuth }}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortalAuth = () => {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('usePortalAuth must be used within a PortalAuthProvider');
  }
  return context;
};
